import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { cn } from '../../../utils/class-utils';

@Component({
    selector: 'jjames-loading',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div [class]="containerClasses">
      <!-- Spinner -->
      @if (type === 'spinner') {
        <div [class]="spinnerClasses">
          <svg class="animate-spin h-full w-full text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      }
      
      <!-- Dots -->
      @if (type === 'dots') {
        <div class="flex space-x-2">
          @for (dot of [1,2,3]; track dot) {
            <div [class]="dotClasses" [style.animation-delay]="(dot * 0.2) + 's'"></div>
          }
        </div>
      }
      
      <!-- Pulse -->
      @if (type === 'pulse') {
        <div [class]="pulseClasses"></div>
      }
      
      <!-- Text -->
      @if (text) {
        <p [class]="textClasses">{{ text }}</p>
      }
    </div>
  `,
    styles: [`
    .dot-bounce {
      animation: dot-bounce 1.4s infinite ease-in-out both;
    }
    
    @keyframes dot-bounce {
      0%, 80%, 100% { 
        transform: scale(0);
      } 40% { 
        transform: scale(1);
      }
    }
    
    .pulse-animation {
      animation: pulse-animation 1.5s infinite;
    }
    
    @keyframes pulse-animation {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `]
})
export class JJamesLoadingComponent {
    @Input() type: 'spinner' | 'dots' | 'pulse' = 'spinner';
    @Input() size: 'sm' | 'md' | 'lg' = 'md';
    @Input() color: 'primary' | 'secondary' | 'white' = 'primary';
    @Input() text?: string;
    @Input() center = false;

    get containerClasses(): string {
        const baseClasses = 'flex items-center';
        const centerClasses = this.center ? 'justify-center' : '';
        const directionClasses = this.text ? 'flex-col space-y-2' : '';

        return cn(baseClasses, centerClasses, directionClasses);
    }

    get spinnerClasses(): string {
        const sizeClasses = this.getSizeClasses();
        const colorClasses = this.getColorClasses();

        return cn(sizeClasses, colorClasses);
    }

    get dotClasses(): string {
        const baseClasses = 'dot-bounce rounded-full';
        const sizeClasses = this.getDotSizeClasses();
        const colorClasses = this.getDotColorClasses();

        return cn(baseClasses, sizeClasses, colorClasses);
    }

    get pulseClasses(): string {
        const baseClasses = 'pulse-animation rounded';
        const sizeClasses = this.getPulseSizeClasses();
        const colorClasses = this.getPulseColorClasses();

        return cn(baseClasses, sizeClasses, colorClasses);
    }

    get textClasses(): string {
        const baseClasses = 'text-sm';
        const colorClasses = this.getTextColorClasses();

        return cn(baseClasses, colorClasses);
    }

    private getSizeClasses(): string {
        const sizes = {
            sm: 'w-4 h-4',
            md: 'w-6 h-6',
            lg: 'w-8 h-8'
        };
        return sizes[this.size];
    }

    private getDotSizeClasses(): string {
        const sizes = {
            sm: 'w-2 h-2',
            md: 'w-3 h-3',
            lg: 'w-4 h-4'
        };
        return sizes[this.size];
    }

    private getPulseSizeClasses(): string {
        const sizes = {
            sm: 'w-8 h-8',
            md: 'w-12 h-12',
            lg: 'w-16 h-16'
        };
        return sizes[this.size];
    }

    private getColorClasses(): string {
        const colors = {
            primary: 'text-blue-600',
            secondary: 'text-gray-600',
            white: 'text-white'
        };
        return colors[this.color];
    }

    private getDotColorClasses(): string {
        const colors = {
            primary: 'bg-blue-600',
            secondary: 'bg-gray-600',
            white: 'bg-white'
        };
        return colors[this.color];
    }

    private getPulseColorClasses(): string {
        const colors = {
            primary: 'bg-blue-200',
            secondary: 'bg-gray-200',
            white: 'bg-white bg-opacity-20'
        };
        return colors[this.color];
    }

    private getTextColorClasses(): string {
        const colors = {
            primary: 'text-blue-600',
            secondary: 'text-gray-600',
            white: 'text-white'
        };
        return colors[this.color];
    }
}